/** =====================================
 *              API CONFIG
 *  Автоопределение API_BASE + адаптер
 *  ===================================== */
export const API_BASE = (() => {
  try {
    if (typeof window !== "undefined" && window.__API_BASE__) return window.__API_BASE__;
    if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE)
      return import.meta.env.VITE_API_BASE;
    if (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE)
      return process.env.REACT_APP_API_BASE;
    const tag = typeof document !== "undefined" && document.querySelector('meta[name="api-base"]');
    if (tag?.content) return tag.content;
  } catch {}
  try {
    if (typeof window !== "undefined" && window.location && window.location.hostname === "localhost") {
      return "http://localhost:8000";
    }
  } catch {}
  return "https://api.noesa.ai";
})();

// База RU (для ПДн: auth/records/media/consents/feed/events)
export const API_BASE_RU = (() => {
  try {
    if (typeof window !== "undefined" && window.__API_BASE_RU__) return window.__API_BASE_RU__;
    if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE_RU)
      return import.meta.env.VITE_API_BASE_RU;
    if (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_RU)
      return process.env.REACT_APP_API_BASE_RU;
  } catch {}
  return API_BASE;
})();

// Автовыбор способа аутентификации по контексту
// В кросс-сайтовом сценарии (API и страница на разных registrable domains)
// браузеры могут блокировать cookie — используем Bearer токен.
// Важно: разные поддомены одного домена (noesa.ai vs api.noesa.ai) — это тот же "site",
// cookie там работают нормально и безопаснее (httpOnly).
const DEFAULT_TOKEN_KIND = (() => {
  try {
    if (typeof window === "undefined") return "cookie";
    const apiUrl = new URL(API_BASE);
    const pageHost = window.location?.hostname;
    const apiHost = apiUrl.hostname;
    const norm = (h) => String(h || "").trim().toLowerCase();
    const isIp = (h) => {
      const s = norm(h);
      // IPv4 or IPv6 (very rough, but enough for choosing cookie/bearer)
      return /^\d{1,3}(\.\d{1,3}){3}$/.test(s) || s.includes(":");
    };
    const siteKey = (h) => {
      const s = norm(h);
      if (!s) return "";
      if (s === "localhost") return "localhost";
      if (isIp(s)) return s;
      const parts = s.split(".").filter(Boolean);
      if (parts.length <= 2) return s;
      // NOTE: this is a pragmatic eTLD+1 approximation; good enough for our prod domains.
      return parts.slice(-2).join(".");
    };
    const sameSite = siteKey(apiHost) && siteKey(pageHost) && siteKey(apiHost) === siteKey(pageHost);
    return sameSite ? "cookie" : "bearer";
  } catch {
    return "cookie";
  }
})();

// Значения по умолчанию (совместимы с беком под noesa.ai)
export const DEFAULT_CFG = {
  prefix: "",
  tokenKind: DEFAULT_TOKEN_KIND,
  endpoints: {
    auth: {
      login: "/auth/login",
      register: "/auth/register",
      me: "/auth/me",
      changePassword: "/auth/change_password",
      logoutAll: "/auth/logout_all",
    },
    records: {
      dates: "/records/dates",
      byDate: "/records/by_date",
      add: "/records/add",
      item: (id) => `/records/${id}`,
    },
    media: { file: (filename) => `/media/${filename}` },
    chat: {
      send: "/chat/send",
      history: "/chat/history",
      modes: "/chat/modes",
      voice: "/chat/voice",
    },
    voice: { stt: "/voice/stt" },
    motivation: "/motivation",
    analytics: {
      stats: "/analytics/stats",
      streak: "/analytics/streak",
      moodTimeline: "/analytics/mood_timeline",
      topicsTrends: "/analytics/topics_trends",
      heatmap: "/analytics/productivity_heatmap",
      exportPdf: "/analytics/export/pdf",
      recommendations: "/analytics/recommendations",
    },
    settings: "/settings",
    user: { deleteData: "/user/delete_data" },
    data: { export: "/data/export" },
    integrations: { connect: "/integrations/connect" },
  },
};

export const BACKEND_CFG = (() => {
  try {
    return { ...DEFAULT_CFG, ...(window.__API_CFG__ || {}) };
  } catch {
    return DEFAULT_CFG;
  }
})();

/** Универсальные утилиты API */
export function buildUrl(path) {
  if (!path) return API_BASE;
  const isAbs = /^(https?:)?\/\//i.test(path);
  const withPrefix = BACKEND_CFG.prefix
    ? `${BACKEND_CFG.prefix}${path.startsWith("/") ? "" : "/"}${path}`
    : path;
  if (isAbs) return path;

  // Роутинг по префиксам: всё ПДн и пользовательские данные — в RU
  const ruPrefixes = [
    "/measurement", // account-linked product facts stay with the RU account database
    "/auth",
    "/records",
    "/media",
    "/consents",
    "/feed",
    "/events",
    "/integrations",
    "/shadow_analyst",
    "/chat", // route all chat endpoints to RU (history, send, stream, voice)
    "/agent-runs",
    "/voice",
    "/chat/history",
    "/analytics",
    "/settings",
    "/user",
  ];
  const base = ruPrefixes.some((p) => withPrefix.startsWith(p)) ? API_BASE_RU : API_BASE;
  return `${base}${withPrefix.startsWith("/") ? "" : "/"}${withPrefix}`;
}

export const mediaSvc = {
  buildUrl(urlOrPath) {
    if (!urlOrPath) return "";
    const abs = /^(https?:)?\/\//i.test(urlOrPath);
    return abs ? urlOrPath : buildUrl(urlOrPath);
  },
};
